// Folders & Document Types — the admin screen for doc_folders / doc_types (Utilities → Table Management).
// The one place the document vocabulary is edited; every pick list, the Intake Folder Reference, the Documents
// screens and filing read these tables (utils/docVocab). See docs/folders-and-file-types-report.md.
//
// ⛔ KEYS NEVER CHANGE; NAMES DO. Documents store folder_key / type_key, so a rename shows everywhere at once and
// orphans nothing. legacy_name (the string old writers sent) is never edited here.
// ⛔ EDITING IS DATABASE-ENFORCED. RLS lets only staff with ntrg_staff.manages_doc_types write these tables; anyone
// else reads them. Every write checks the affected-row count, because a write the policy blocks returns 0 rows
// and NO error — the documented silent-success class.
// ⛔ LOCKED rows are keys the code depends on (DOC_KEYS in utils/docVocab): renamable, never retired, moved,
// re-routed (scan_as) or deleted. A folder/type any document uses cannot be deleted (the FK refuses) — retire it.
// A type moves only between folders of the same level (property, client or consultant): its filed documents
// move with it (display only — R2 objects stay put), and a property document must not land in a client folder.
// ⛔ WHO MAY EDIT IS ASKED OF THE DATABASE, via the same manages_doc_types() function the write policies call — never
// re-derived here. It used to read ntrg_staff after a supabase.auth.getUser() round-trip and swallow every failure,
// so a slow, failed or mid-token-refresh check showed "Read-only" to the one person who can edit, with no trace.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { loadDocVocab } from '../utils/docVocab';

const bySort = (a, b) => (a.sort - b.sort) || a.name.localeCompare(b.name);
const slug = (name) => String(name || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'item';
const SCAN_LABEL = { none: 'File only', assessment: 'Notice scanner', taxbill: 'Bill scanner' };
const LEVEL_SECTIONS = [['property', 'Property folders', 'var(--red)'], ['client', 'Client folders', '#a78bfa'], ['consultant', 'Consultant folders', '#0e7490']];

export default function DocFoldersTable({ c, s }) {
  const [folders, setFolders] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [rights, setRights] = useState({ checked: false, error: null });   // the edit-rights answer, or why there is none
  const [open, setOpen] = useState(null);          // expanded folder key
  const [editing, setEditing] = useState(null);    // { table, key, name } — an inline rename in progress
  const [adding, setAdding] = useState(null);      // { kind:'folder', level } | { kind:'type', folderKey }
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);            // { tone:'error'|'info', text }

  const load = useCallback(async () => {
    const [{ data: f, error: fe }, { data: t, error: te }] = await Promise.all([
      supabase.from('doc_folders').select('*'),
      supabase.from('doc_types').select('*'),
    ]);
    if (fe || te) setMsg({ tone: 'error', text: `Could not load folders/types: ${(fe || te).message}` });
    setFolders([...(f || [])].sort(bySort));
    setTypes([...(t || [])].sort(bySort));
    setLoading(false);
    loadDocVocab({ fresh: true }).catch(() => {});   // the shared cache: other screens get the change on their next load
  }, []);

  useEffect(() => {
    load();
    supabase.rpc('manages_doc_types').then(({ data, error }) => {
      setCanEdit(!error && data === true);
      setRights({ checked: true, error: error ? error.message : null });
    }, (e) => setRights({ checked: true, error: e.message || 'the check did not complete' }));
  }, [load]);

  const friendly = (e) => e.code === '23505' ? 'that name is already in use'
    : e.code === '23503' ? 'documents still use it — retire it instead'
    : e.message;
  const fail = (what, error) => { setMsg({ tone: 'error', text: `${what} — not saved: ${error ? friendly(error) : '0 rows changed (no permission?)'}` }); return false; };

  // Every update: affected-row check, then reload.
  async function update(table, key, patch, what) {
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.from(table).update({ ...patch, updated_at: new Date().toISOString() }).eq('key', key).select('key');
    setBusy(false);
    if (error || !data?.length) return fail(what, error);
    await load();
    return true;
  }

  const folderTypes = (folderKey) => types.filter(t => t.folder_key === folderKey);
  const docCount = async (column, key) => {
    const [{ count: a, error: ae }, { count: b, error: be }] = await Promise.all([
      supabase.from('file_exchange').select('id', { count: 'exact', head: true }).eq(column, key),
      supabase.from('parcel_documents').select('id', { count: 'exact', head: true }).eq(column, key),
    ]);
    if (ae || be) throw (ae || be);
    return (a || 0) + (b || 0);
  };
  const uniqueKey = (base, taken) => { let k = base, i = 2; while (taken.has(k)) k = `${base}_${i++}`; return k; };

  // Reorder by swapping sort with the neighbour in the same list (folders of a level, or types of a folder).
  async function move(table, list, idx, dir) {
    const a = list[idx], b = list[idx + dir];
    if (!a || !b) return;
    const aSort = b.sort === a.sort ? a.sort + dir : b.sort;
    const bSort = a.sort;
    if (!(await update(table, a.key, { sort: aSort }, `Reorder "${a.name}"`))) return;
    await update(table, b.key, { sort: bSort }, `Reorder "${b.name}"`);
  }

  async function saveRename() {
    const name = String(editing.name || '').trim();
    if (!name) { setMsg({ tone: 'error', text: 'A name can’t be empty.' }); return; }
    if (await update(editing.table, editing.key, { name }, `Rename to "${name}"`)) setEditing(null);
  }

  async function retire(table, row) {
    const next = !row.active;
    if (!next && !window.confirm(`Retire "${row.name}"? It disappears from every pick list. Documents already filed keep it and still show under it.`)) return;
    await update(table, row.key, { active: next }, `${next ? 'Restore' : 'Retire'} "${row.name}"`);
  }

  // Moving a type moves its FILED documents with it (display only — the stored files are not relocated in R2).
  // Documents of that type still sitting in an Intake pile state are not filed in the old folder, so they stay.
  async function moveType(t, toKey) {
    const from = folders.find(f => f.key === t.folder_key), to = folders.find(f => f.key === toKey);
    if (!to || toKey === t.folder_key) return;
    const countIn = (table) => supabase.from(table).select('id', { count: 'exact', head: true }).eq('type_key', t.key).eq('folder_key', t.folder_key);
    const [fxc, pdc] = await Promise.all([countIn('file_exchange'), countIn('parcel_documents')]);
    if (fxc.error || pdc.error) { setMsg({ tone: 'error', text: `Could not count its documents: ${(fxc.error || pdc.error).message}` }); return; }
    const n = fxc.count || 0, nLinks = pdc.count || 0;
    if (!window.confirm(`Move "${t.name}" from ${from?.name} to ${to.name}?\n\n${n} filed document(s) of this type move with it. The stored files are not relocated — only where they show.`)) return;
    if (!(await update('doc_types', t.key, { folder_key: toKey }, `Move "${t.name}"`))) return;
    setBusy(true);
    const [fx, pd] = await Promise.all([
      supabase.from('file_exchange').update({ folder_key: toKey }).eq('type_key', t.key).eq('folder_key', t.folder_key).select('id'),
      supabase.from('parcel_documents').update({ folder_key: toKey }).eq('type_key', t.key).eq('folder_key', t.folder_key).select('id'),
    ]);
    setBusy(false);
    if (fx.error || pd.error) { setMsg({ tone: 'error', text: `"${t.name}" moved, but its documents did not: ${(fx.error || pd.error).message}. Moving it again finishes the job.` }); return; }
    // Both counts checked: a policy-blocked update returns fewer rows and no error.
    if ((fx.data || []).length !== n || (pd.data || []).length !== nLinks) {
      setMsg({ tone: 'error', text: `"${t.name}" moved; ${(fx.data || []).length} of ${n} documents (${(pd.data || []).length} of ${nLinks} parcel links) followed — the rest were blocked. Moving it again finishes the job.` });
      return;
    }
    setMsg({ tone: 'info', text: `Moved "${t.name}" to ${to.name}, with ${n} document(s).` });
  }

  async function remove(table, row, column) {
    if (row.locked) return;
    try {
      const used = await docCount(column, row.key);
      const inFolder = table === 'doc_folders' ? folderTypes(row.key).length : 0;
      if (used || inFolder) {
        setMsg({ tone: 'error', text: `"${row.name}" can’t be deleted: ${used ? `${used} document(s) use it` : `it still holds ${inFolder} type(s)`}. Retire it instead.` });
        return;
      }
    } catch (e) { setMsg({ tone: 'error', text: `Could not check whether it is used: ${e.message}` }); return; }
    if (!window.confirm(`Delete "${row.name}" permanently? Nothing uses it.`)) return;
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.from(table).delete().eq('key', row.key).select('key');
    setBusy(false);
    if (error || !data?.length) return fail(`Delete "${row.name}"`, error);
    await load();
  }

  async function saveAdd() {
    const name = String(draft.name || '').trim();
    if (!name) { setMsg({ tone: 'error', text: 'Give it a name.' }); return; }
    setBusy(true); setMsg(null);
    let res;
    if (adding.kind === 'folder') {
      const peers = folders.filter(f => f.level === adding.level && !f.system);
      // Names are unique within a level; the STORED name (legacy_name) is unique everywhere — doc_keys_sync maps stored
      // names to keys by it. A name another level already stores gets the level in front ("Consultant Contracts").
      const storedTaken = new Set(folders.map(f => f.legacy_name.toLowerCase()));
      const legacy = storedTaken.has(name.toLowerCase()) ? `${adding.level[0].toUpperCase()}${adding.level.slice(1)} ${name}` : name;
      res = await supabase.from('doc_folders').insert({
        key: uniqueKey(slug(name), new Set(folders.map(f => f.key))), name, legacy_name: legacy,
        level: adding.level, has_years: draft.has_years !== false, sort: Math.max(0, ...peers.map(f => f.sort)) + 10,
      }).select('key');
    } else {
      const peers = folderTypes(adding.folderKey);
      res = await supabase.from('doc_types').insert({
        key: uniqueKey(slug(name), new Set(types.map(t => t.key))), folder_key: adding.folderKey, name, legacy_name: name,
        scan_as: draft.scan_as || 'none', bill_type: draft.scan_as === 'taxbill' ? (draft.bill_type || null) : null,
        sort: Math.max(0, ...peers.map(t => t.sort)) + 10,
      }).select('key');
    }
    setBusy(false);
    if (res.error || !res.data?.length) return fail(`Add "${name}"`, res.error);
    setAdding(null); setDraft({});
    await load();
  }

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  const small = { ...s.btnGhost, padding: '3px 8px', fontSize: '11px' };
  const lockBadge = <span title="The app relies on this one: it can be renamed, not retired, moved or deleted" style={{ ...s.badge('148,163,184'), marginLeft: '6px' }}>🔒 locked</span>;
  const input = { padding: '5px 8px', fontSize: '12px', border: `1px solid ${c.inputBorder}`, borderRadius: '6px', background: c.input, color: c.text };
  const nameCell = (table, row) => editing && editing.table === table && editing.key === row.key ? (
    <span style={{ display: 'inline-flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
      <input autoFocus value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
        onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(null); }} style={input} />
      <button style={small} disabled={busy} onClick={saveRename}>Save</button>
      <button style={small} onClick={() => setEditing(null)}>✕</button>
    </span>
  ) : (
    <span>{row.name}{canEdit && <button style={{ ...small, marginLeft: '8px' }} onClick={e => { e.stopPropagation(); setEditing({ table, key: row.key, name: row.name }); }}>✎</button>}</span>
  );

  // Render FUNCTIONS, not inner components: a component defined in here is a new type on every render, so its inputs
  // would remount (and lose focus) on each keystroke.
  function typeRows(folder) {
    const list = folderTypes(folder.key);
    return (
      <tr><td colSpan="5" style={{ padding: '0 16px 12px 40px', borderBottom: `1px solid ${c.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Type', 'Scan', 'Bill type', 'Folder', ''].map(h => <th key={h} style={{ ...s.th, background: 'transparent' }}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map((t, i) => (
              <tr key={t.key} style={{ opacity: t.active ? 1 : 0.55 }}>
                <td style={s.td}>{nameCell('doc_types', t)}{t.locked && lockBadge}{!t.active && <span style={{ ...s.badge('248,113,113'), marginLeft: '6px' }}>Retired</span>}</td>
                <td style={s.td}>{canEdit && !t.locked
                  ? <select value={t.scan_as} disabled={busy} style={input} onChange={e => update('doc_types', t.key, { scan_as: e.target.value, ...(e.target.value !== 'taxbill' ? { bill_type: null } : {}) }, `Scan setting for "${t.name}"`)}>
                      {Object.entries(SCAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  : SCAN_LABEL[t.scan_as]}</td>
                <td style={s.td}>{t.scan_as !== 'taxbill' ? '—' : canEdit && !t.locked
                  ? <select value={t.bill_type || ''} disabled={busy} style={input} onChange={e => update('doc_types', t.key, { bill_type: e.target.value || null }, `Bill type for "${t.name}"`)}>
                      <option value="">(from the bill)</option><option value="Supplemental">Supplemental</option><option value="Delinquent">Delinquent</option>
                    </select>
                  : (t.bill_type || '(from the bill)')}</td>
                <td style={s.td}>{canEdit && !t.locked
                  ? <select value={t.folder_key} disabled={busy} style={input} onChange={e => moveType(t, e.target.value)}>
                      {folders.filter(f => !f.system && f.level === folder.level && (f.active || f.key === t.folder_key)).map(f => <option key={f.key} value={f.key}>{f.name}</option>)}
                    </select>
                  : folder.name}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{canEdit && <>
                  <button style={small} disabled={busy || i === 0} onClick={() => move('doc_types', list, i, -1)}>↑</button>
                  <button style={{ ...small, marginLeft: '4px' }} disabled={busy || i === list.length - 1} onClick={() => move('doc_types', list, i, 1)}>↓</button>
                  {!t.locked && <button style={{ ...small, marginLeft: '6px' }} disabled={busy} onClick={() => retire('doc_types', t)}>{t.active ? 'Retire' : 'Restore'}</button>}
                  {!t.locked && <button style={{ ...small, marginLeft: '4px', color: '#f87171' }} disabled={busy} onClick={() => remove('doc_types', t, 'type_key')}>Delete</button>}
                </>}</td>
              </tr>
            ))}
            {canEdit && (adding?.kind === 'type' && adding.folderKey === folder.key ? (
              <tr><td colSpan="5" style={s.td}>
                <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input autoFocus placeholder="Type name" value={draft.name || ''} onChange={e => setDraft({ ...draft, name: e.target.value })} style={input} />
                  <select value={draft.scan_as || 'none'} onChange={e => setDraft({ ...draft, scan_as: e.target.value })} style={input}>
                    {Object.entries(SCAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <button style={s.btn} disabled={busy} onClick={saveAdd}>Add type</button>
                  <button style={small} onClick={() => { setAdding(null); setDraft({}); }}>Cancel</button>
                </span>
              </td></tr>
            ) : (
              <tr><td colSpan="5" style={{ ...s.td, borderBottom: 'none' }}>
                <button style={small} onClick={() => { setAdding({ kind: 'type', folderKey: folder.key }); setDraft({}); }}>+ Add type to {folder.name}</button>
              </td></tr>
            ))}
          </tbody>
        </table>
      </td></tr>
    );
  }

  function levelRows(level, title, color) {
    const list = folders.filter(f => f.level === level && !f.system);
    return (
      <React.Fragment key={level}>
        <tr><td colSpan="5" style={{ ...s.td, fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color }}>{title}</td></tr>
        {list.map((f, i) => (
          <React.Fragment key={f.key}>
            <tr style={{ cursor: 'pointer', opacity: f.active ? 1 : 0.55 }} onClick={() => setOpen(open === f.key ? null : f.key)}
              onMouseEnter={e => e.currentTarget.style.background = c.rowHover} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <td style={{ ...s.td, fontWeight: '600' }}>{open === f.key ? '▾' : '▸'} {nameCell('doc_folders', f)}{f.locked && lockBadge}{!f.active && <span style={{ ...s.badge('248,113,113'), marginLeft: '6px' }}>Retired</span>}</td>
              <td style={{ ...s.td, color: c.muted }}>{folderTypes(f.key).filter(t => t.active).length} type(s)</td>
              <td style={s.td} onClick={e => e.stopPropagation()}>
                <label style={{ fontSize: '12px', color: c.muted, display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                  <input type="checkbox" checked={f.has_years} disabled={!canEdit || busy} onChange={e => update('doc_folders', f.key, { has_years: e.target.checked }, `Year folders for "${f.name}"`)} />
                  Year folders{f.has_years && f.year_label ? ` (${f.year_label})` : ''}
                </label>
              </td>
              <td style={{ ...s.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>{canEdit && <>
                <button style={small} disabled={busy || i === 0} onClick={() => move('doc_folders', list, i, -1)}>↑</button>
                <button style={{ ...small, marginLeft: '4px' }} disabled={busy || i === list.length - 1} onClick={() => move('doc_folders', list, i, 1)}>↓</button>
                {!f.locked && <button style={{ ...small, marginLeft: '6px' }} disabled={busy} onClick={() => retire('doc_folders', f)}>{f.active ? 'Retire' : 'Restore'}</button>}
                {!f.locked && <button style={{ ...small, marginLeft: '4px', color: '#f87171' }} disabled={busy} onClick={() => remove('doc_folders', f, 'folder_key')}>Delete</button>}
              </>}</td>
              <td style={s.td} />
            </tr>
            {open === f.key && typeRows(f)}
          </React.Fragment>
        ))}
        {canEdit && (adding?.kind === 'folder' && adding.level === level ? (
          <tr><td colSpan="5" style={s.td}>
            <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
              <input autoFocus placeholder="Folder name" value={draft.name || ''} onChange={e => setDraft({ ...draft, name: e.target.value })} style={input} />
              <label style={{ fontSize: '12px', color: c.muted, display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                <input type="checkbox" checked={draft.has_years !== false} onChange={e => setDraft({ ...draft, has_years: e.target.checked })} /> Year folders
              </label>
              <button style={s.btn} disabled={busy} onClick={saveAdd}>Add folder</button>
              <button style={small} onClick={() => { setAdding(null); setDraft({}); }}>Cancel</button>
            </span>
          </td></tr>
        ) : (
          <tr><td colSpan="5" style={s.td}><button style={small} onClick={() => { setAdding({ kind: 'folder', level }); setDraft({}); }}>+ Add {level} folder</button></td></tr>
        ))}
      </React.Fragment>
    );
  }

  return (
    <div style={s.card}>
      <div style={s.cardH}>
        <div>
          <div style={s.cardT}>Folders & Document Types</div>
          <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
            The one list behind every document type picker, the Folder Reference and the Documents screens.
            {!rights.checked ? ' Checking your edit rights…'
              : rights.error ? ` Your edit rights could not be checked (${rights.error}) — read-only until you reload the page.`
              : canEdit ? ' Click a folder to manage its types.' : ' Read-only — only staff with document-type admin rights can edit.'}
          </div>
        </div>
      </div>
      {msg && <div style={{ padding: '10px 18px', fontSize: '12px', fontWeight: '600', color: msg.tone === 'error' ? 'var(--red)' : '#34d399', borderBottom: `1px solid ${c.border}` }}>{msg.text}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {LEVEL_SECTIONS.map(([level, title, color]) => levelRows(level, title, color))}
          <tr><td colSpan="5" style={{ ...s.td, color: c.muted, fontSize: '12px' }}>
            Intake pile states (not folders, fixed): {folders.filter(f => f.system).map(f => f.name).join(' · ')}
          </td></tr>
        </tbody>
      </table>
    </div>
  );
}
